import { useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Xmark } from '@peacecover/icon';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { useGetSceneOriginalEpisodes } from '../../domains/scene/ui/hooks/useGetSceneOriginalEpisodes';
import { useUpdateReadingProgress } from '../../domains/scene/ui/hooks/useUpdateReadingProgress';
import { useScrollProgressUI } from './hooks/useScrollProgressUI';
import { useScrollRestore } from './hooks/useScrollRestore';
import EpisodeCards from './components/EpisodeCards';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';

const EPISODES_LIMIT = 30;

const SceneOriginalEpisodesPage = () => {
  const handleHistoryBack = useHandleHistoryBack();
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();

  const targetEpisodeId = searchParams.get('episodeId')?.trim() || undefined;
  const targetCursor = searchParams.get('cursor');
  const targetCharOffset = searchParams.get('charOffset');

  const { data, isLoading } = useGetSceneOriginalEpisodes(sceneId ?? '', {
    limit: EPISODES_LIMIT,
    direction: 'after'
  });

  const episodes = useMemo(
    () => data?.pages.flatMap((p) => p.sceneEpisodes ?? []) ?? [],
    [data?.pages]
  );
  const totalEpisodes = data?.pages?.[0]?.meta?.pagination?.totalCount ?? episodes.length;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { mutate: updateReadingProgress } = useUpdateReadingProgress();

  const { progressPercent, currentEpisodeNumber, canScrollLeft, canScrollRight } =
    useScrollProgressUI({
      scrollContainerRef,
      sceneId: sceneId ?? undefined,
      updateReadingProgress,
      episodes
    });

  const { isScrollRestoring, overlayMinTimePassed } = useScrollRestore({
    scrollContainerRef,
    isLoading,
    episodesLength: episodes.length,
    targetEpisodeId,
    targetCursor,
    targetCharOffset
  });

  const handlePrevPage = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !canScrollLeft) return;
    container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
  }, [canScrollLeft]);

  const handleNextPage = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !canScrollRight) return;
    container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
  }, [canScrollRight]);

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] bg-background-light dark:bg-background-dark">
      <header className="relative z-50 flex items-center justify-between px-[16px] py-[10px] bg-background-light dark:bg-background-dark">
        <button type="button" onClick={handleHistoryBack}>
          <Xmark className="text-text-main-lv1" />
        </button>
        <p className="text-[17px] font-bold leading-[20px] text-center text-text-main-lv1 whitespace-nowrap absolute left-1/2 -translate-x-1/2">
          원본 에피소드
        </p>
      </header>

      <div className="relative flex-1 min-h-0 w-screen overflow-hidden pb-[calc(env(safe-area-inset-bottom)+74px)]">
        {targetEpisodeId && (isLoading || isScrollRestoring || !overlayMinTimePassed) && (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center bg-background-light dark:bg-background-dark"
            aria-live="polite"
          >
            <GradientLoadingSpinner size={36} />
          </div>
        )}
        <div
          ref={scrollContainerRef}
          className="relative flex h-full w-screen flex-row items-start overflow-x-auto overflow-y-hidden snap-x snap-mandatory hiddenScrollBar"
          style={{
            scrollSnapStop: 'always',
            scrollBehavior: 'smooth'
          }}
        >
          {isLoading ? (
            <div className="w-screen px-[17px] py-[16px] flex items-center justify-center text-[14px] text-text-main-lv3">
              로딩 중...
            </div>
          ) : episodes.length === 0 ? (
            <div className="w-screen px-[17px] py-[16px] text-center text-[14px] text-text-main-lv3">
              에피소드가 없습니다.
            </div>
          ) : (
            episodes.map((episode) => (
              <EpisodeCards
                key={episode.sceneEpisodeId}
                sceneId={sceneId ?? ''}
                episode={episode}
              />
            ))
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-[20px] flex gap-[34px] items-center justify-center pt-[16px] pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-white dark:bg-pure-black shadow-[0_1px_5.8px_0_rgba(0,0,0,0.12)]">
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={!canScrollLeft}
          className="flex-shrink-0 p-[6px] rounded-full bg-separator1 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
        >
          <ArrowLeft size={24} className="text-text-main-lv4" />
        </button>
        <div className="flex flex-col items-center gap-[8px] justify-center">
          <div className="relative w-[143px]">
            <div className="w-full h-[6px] bg-black-opacity-200 dark:bg-white-opacity-200 rounded-[20px]" />
            <div
              className="absolute top-0 left-0 h-[6px] overflow-hidden rounded-[20px]"
              style={{ width: `${progressPercent}%` }}
            >
              <div
                className="h-full w-[143px] rounded-[20px] bg-[linear-gradient(90deg,#41F7A2_0%,#5C3DFA_100%)]"
                aria-hidden
              />
            </div>
          </div>
          <div className="flex items-center">
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3 flex-1 text-center min-w-0">
              {totalEpisodes > 0
                ? `${Math.min(currentEpisodeNumber, totalEpisodes)} / ${totalEpisodes} 에피소드`
                : '에피소드 없음'}
            </p>
            <div className="w-[1px] h-[10px] bg-[#D9D9D9] ml-[7px] mr-[10px]" />
            <p className="text-[12px] leading-[14px] text-text-main-lv3 font-semibold whitespace-nowrap">
              {progressPercent}%
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={!canScrollRight}
          className="flex-shrink-0 p-[6px] rounded-full bg-separator1 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
        >
          <ArrowRight size={24} className="text-text-main-lv4" />
        </button>
      </div>
    </div>
  );
};

export default SceneOriginalEpisodesPage;
